import { useCallback, useEffect, useState } from 'react'
import { Form } from 'components/Wizards/Form/Form'
import { Formik } from 'formik'
import { multiStep } from 'components/Wizards/MultiStepWizard/multiStep'
import { StepProps } from 'components/Wizards/MultiStepWizard/Types'
import { Field } from 'components/Wizards/Wizard/Form/Field'
import { useCreateFieldsetCompletion } from 'hooks/queries/workspaces/useCreateFieldsetCompletion'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { Button } from 'blaze-ui/Button'
import { Field as BaseField } from 'components/Wizards/Field/Field'
import { ArrowLeft, ArrowRight } from 'blaze-ui/icons'
import { PollFieldsetCompletion } from 'components/PollFieldsetCompletion/PollFieldsetCompletion'
import { AIJobStatus, VisualGenerateRequest } from 'types'
import { useWizardSchema } from 'components/Wizards/Wizard/WizardContext'
import { MultiStepSchema } from 'components/Wizards/schemas/Types'
import { VisualElement } from 'api/clients/VisualElementClient'
import { FilePathFormikField } from 'components/Wizards/FilePathFormikField/FilePathFormikField'
import Styles from './TemplateAndCaption.module.scss'
import { CaptionField } from 'components/Wizards/DesignWizard/TemplateAndCaption/CaptionField'
import { TemplateField } from 'components/Wizards/DesignWizard/TemplateAndCaption/TemplateField'
import { Heading } from 'blaze-ui/Heading/Heading'
import { ValuesObserver } from './ValuesObserver'
import { Text } from 'blaze-ui/Text/Text'
import { Icon } from 'blaze-ui/icons/Types'
import { reportError } from 'utils/error'

import { DesignChannel } from 'components/NewFileModal/Types'
import { Values as DesktopValues } from 'components/Wizards/DesktopWizards/Types'

export interface Values {
  caption: string
  source_visual_element_id?: number
  filename?: string
  brand_kit_id?: number
  brand_visual_kit_id?: number
}

interface Props {
  showBrandKitInput?: boolean
  onBrandKitChange?: (brandKitId?: number) => void
  isFileNameLoading?: boolean
  templateFieldDescription?: string
  submitLabel?: string
  previousLabel?: string
  previousStepButtonIcon?: Icon | null
  onCancel?: () => void
  onDesignCreated?: (design: VisualGenerateRequest['almanac_file'] | undefined) => void
}

export const TemplateAndCaption = multiStep<StepProps & Props>((props) => {
  const {
    schema,
    initialValues,
    onSubmit,
    onBack,
    onCancel,
    showBrandKitInput,
    onBrandKitChange,
    isFileNameLoading,
    templateFieldDescription = 'Suggestions are based on the topic of your post',
    onChange,
    previousLabel = 'Purpose & Goals',
    previousStepButtonIcon = ArrowLeft,
  } = props

  const [fieldsCompleted, setFieldsCompleted] = useState(false)
  const wizardSchema = useWizardSchema() as MultiStepSchema
  const [selectedTemplateFromModal, setSelectedTemplateFromModal] = useState<VisualElement>()
  const { mutate, data } = useCreateFieldsetCompletion()

  const submitLabel =
    props.submitLabel || (wizardSchema.steps.length === 3 ? schema.describe().meta.submitLabel : 'Generate Post')

  const templateField = Object.entries(schema.fields).find(([name]) => name === 'source_visual_element_id')
  const captionField = Object.entries(schema.fields).find(([name]) => name === 'caption')
  const filenameField = Object.entries(schema.fields).find(([name]) => name === 'project')
  const fields = Object.entries(schema.fields).filter(([name]) => ['cross_post_accounts'].includes(name))

  const captionAboveTemplate = [
    DesignChannel.LinkedInPost,
    DesignChannel.TwitterPost,
    DesignChannel.FacebookPost,
  ].includes(wizardSchema.meta.channel as DesignChannel)

  const handleError = useCallback(() => {
    showErrorMessageToast('Blaze was unable to generate the data. Please contact support if this issue persists.')
    onBack?.()
  }, [onBack])

  useEffect(() => {
    if (!initialValues.topic || fieldsCompleted) return
    const fields = !captionField ? ['filename'] : ['caption', 'filename']

    mutate(
      {
        fields,
        completedFields: {
          ...initialValues,
        },
        generationType: wizardSchema.meta.channel,
      },
      {
        onError: (error) => {
          reportError(error)
          handleError()
        },
      },
    )
  }, [fieldsCompleted, initialValues.topic]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleFormikSubmit = async (values: DesktopValues) => {
    return onSubmit(values)
  }

  return (
    <Formik
      validationSchema={schema}
      onSubmit={handleFormikSubmit}
      initialValues={schema.cast({ ...schema.getDefault(), ...initialValues }, { assert: false }) as DesktopValues}
      enableReinitialize
    >
      {({ setFieldValue, values, submitForm, isSubmitting }) => {
        const fieldValues = values as Values

        const captionField = (
          <CaptionField
            setFieldValue={setFieldValue}
            onUpdate={setFieldsCompleted}
            caption={fieldValues.caption}
            isLoading={!fieldsCompleted}
            showWriteMyOwn
            label="Edit the caption for your post"
          />
        )

        return (
          <>
            <ValuesObserver onChange={() => onChange?.(values)} />
            <PollFieldsetCompletion
              fieldsetCompletionId={data?.fieldset_completion.id}
              onComplete={(fieldset) => {
                if (fieldset.status === AIJobStatus.Failed) {
                  handleError()
                  return
                }

                setFieldsCompleted(true)
                setFieldValue('caption', fieldset.completed_fields['caption'])
                setFieldValue('filename', fieldset.completed_fields['filename'])
              }}
            />
            <Form.Root>
              <Form.Section>
                <Form.SectionContent>
                  <Form.Fields className={Styles.formFields}>
                    <div className={Styles.stepTitle}>
                      <Heading level={1}>{schema.describe().meta.header}</Heading>
                      <Text variant="secondary" color="var(--dark-60)">
                        {templateFieldDescription}
                      </Text>
                    </div>

                    {captionField && captionAboveTemplate && captionField}

                    {templateField && (
                      <TemplateField
                        onSelect={(template) => setFieldValue('source_visual_element_id', template?.id)}
                        topic={initialValues.topic}
                        selectedTemplateFromModal={selectedTemplateFromModal}
                        setSelectedTemplateFromModal={setSelectedTemplateFromModal}
                        isDisabled={isSubmitting || !fieldsCompleted || isFileNameLoading}
                        description=""
                        label="Select a design you'd like to start with"
                        showBrandKitInput={showBrandKitInput}
                        onBrandKitChange={onBrandKitChange}
                        initialValues={initialValues}
                        forceFetchVideoTemplates={schema.describe().meta.forceFetchVideoTemplates}
                      />
                    )}

                    {captionField && !captionAboveTemplate && captionField}

                    {filenameField && (
                      <BaseField label={filenameField[1].describe().meta.label} isRequired>
                        <FilePathFormikField
                          isNameLoading={!fieldsCompleted || isFileNameLoading}
                          placeholder={filenameField[1].describe().meta.placeholder}
                          triggerComponent="button"
                          inputClassName={Styles.filenameInput}
                        />
                      </BaseField>
                    )}

                    {fields.map(([name, schema], i) => (
                      <Field key={name} name={name} schema={schema} autoFocus={i === 0} />
                    ))}
                  </Form.Fields>
                </Form.SectionContent>
              </Form.Section>

              <Form.Actions isSticky>
                {(onBack || onCancel) && (
                  <Form.PreviousButton
                    isDisabled={isSubmitting}
                    frontIcon={previousStepButtonIcon}
                    onPress={() => {
                      setFieldValue('caption', undefined)
                      setFieldValue('filename', undefined)
                      setSelectedTemplateFromModal(undefined)
                      onBack?.() || onCancel?.()
                    }}
                  >
                    {previousLabel}
                  </Form.PreviousButton>
                )}

                <Form.PrimaryActions>
                  <Button
                    size="lg"
                    onPress={submitForm}
                    endIcon={ArrowRight}
                    isDisabled={isSubmitting || !fieldsCompleted}
                    aria-label={submitLabel}
                  >
                    {submitLabel}
                  </Button>
                </Form.PrimaryActions>
              </Form.Actions>
            </Form.Root>
          </>
        )
      }}
    </Formik>
  )
})
